// Deterministic per-codepoint copy formats. All functions are pure and
// handle astral-plane characters (surrogate pairs), ZWJ sequences and
// combining marks correctly by iterating with `Array.from`, which splits
// on code-point boundaries rather than UTF-16 code units.

/** Array of code points (numbers) that make up the symbol. */
export function codePoints(s: string): number[] {
  return Array.from(s).map((c) => c.codePointAt(0)!);
}

/** "U+1F600" / "U+0041 U+030A" — canonical Unicode notation. */
export function uPlusNotation(s: string): string {
  return codePoints(s)
    .map((cp) => `U+${cp.toString(16).toUpperCase().padStart(4, "0")}`)
    .join(" ");
}

/** "&#128512;" — decimal numeric character references. */
export function htmlDecimalEntity(s: string): string {
  return codePoints(s)
    .map((cp) => `&#${cp};`)
    .join("");
}

/** "&#x1F600;" — hex numeric character references. */
export function htmlHexEntity(s: string): string {
  return codePoints(s)
    .map((cp) => `&#x${cp.toString(16).toUpperCase()};`)
    .join("");
}

// Curated subset of the most-used HTML5 named entities. Covers symbols
// the library actually ships (arrows, math, currency, Greek, punctuation,
// hearts, stars, etc.). Anything outside this set falls back to the hex
// numeric entity, which is always valid.
const NAMED_ENTITIES: Record<number, string> = {
  // ASCII reserved
  34: "quot", 38: "amp", 39: "apos", 60: "lt", 62: "gt",
  // Currency & legal
  162: "cent", 163: "pound", 164: "curren", 165: "yen", 8364: "euro",
  169: "copy", 174: "reg", 8482: "trade", 167: "sect", 182: "para",
  // Punctuation & spaces
  160: "nbsp", 8211: "ndash", 8212: "mdash", 8216: "lsquo", 8217: "rsquo",
  8220: "ldquo", 8221: "rdquo", 8230: "hellip", 8226: "bull", 8225: "Dagger",
  8224: "dagger", 8249: "lsaquo", 8250: "rsaquo", 171: "laquo", 187: "raquo",
  // Math
  215: "times", 247: "divide", 177: "plusmn", 8722: "minus", 8730: "radic",
  8734: "infin", 8800: "ne", 8804: "le", 8805: "ge", 8776: "asymp",
  8721: "sum", 8719: "prod", 8747: "int", 8706: "part", 8711: "nabla",
  8736: "ang", 8866: "vdash", 8743: "and", 8744: "or", 8745: "cap",
  8746: "cup", 8712: "isin", 8713: "notin", 8834: "sub", 8835: "sup",
  8709: "empty", 8704: "forall", 8707: "exist", 8773: "cong", 8801: "equiv",
  // Greek (lower)
  945: "alpha", 946: "beta", 947: "gamma", 948: "delta", 949: "epsilon",
  950: "zeta", 951: "eta", 952: "theta", 953: "iota", 954: "kappa",
  955: "lambda", 956: "mu", 957: "nu", 958: "xi", 959: "omicron",
  960: "pi", 961: "rho", 963: "sigma", 964: "tau", 965: "upsilon",
  966: "phi", 967: "chi", 968: "psi", 969: "omega",
  // Greek (upper)
  913: "Alpha", 914: "Beta", 915: "Gamma", 916: "Delta", 917: "Epsilon",
  918: "Zeta", 919: "Eta", 920: "Theta", 921: "Iota", 922: "Kappa",
  923: "Lambda", 924: "Mu", 925: "Nu", 926: "Xi", 927: "Omicron",
  928: "Pi", 929: "Rho", 931: "Sigma", 932: "Tau", 933: "Upsilon",
  934: "Phi", 935: "Chi", 936: "Psi", 937: "Omega",
  // Arrows
  8592: "larr", 8593: "uarr", 8594: "rarr", 8595: "darr", 8596: "harr",
  8629: "crarr", 8656: "lArr", 8657: "uArr", 8658: "rArr", 8659: "dArr",
  8660: "hArr",
  // Misc symbols
  9824: "spades", 9827: "clubs", 9829: "hearts", 9830: "diams",
  9674: "loz", 9001: "lang", 9002: "rang", 8254: "oline",
  176: "deg", 181: "micro", 183: "middot", 168: "uml", 175: "macr",
  166: "brvbar", 172: "not", 173: "shy", 178: "sup2", 179: "sup3",
  185: "sup1", 188: "frac14", 189: "frac12", 190: "frac34",
};

/** Returns the HTML5 named entity (e.g. "&hearts;") if one exists, else null. */
export function htmlNamedEntity(s: string): string | null {
  const cps = codePoints(s);
  if (cps.length !== 1) return null; // named entities are single-codepoint
  const name = NAMED_ENTITIES[cps[0]];
  return name ? `&${name};` : null;
}

/** CSS `content` value, e.g. `content: "\\2728";` (each codepoint hex-escaped). */
export function cssContent(s: string): string {
  const esc = codePoints(s)
    .map((cp) => "\\" + cp.toString(16).toUpperCase())
    .join("");
  return `content: "${esc}";`;
}

/**
 * JavaScript / JSON string escape. Code points in the BMP become `\uXXXX`;
 * astral-plane points emit the UTF-16 surrogate pair so the result is valid
 * in both modern JS and legacy JSON parsers.
 */
export function jsEscape(s: string): string {
  let out = "";
  for (const ch of s) {
    const cp = ch.codePointAt(0)!;
    if (cp > 0xffff) {
      // Surrogate pair
      const high = 0xd800 + ((cp - 0x10000) >> 10);
      const low = 0xdc00 + ((cp - 0x10000) & 0x3ff);
      out += `\\u${high.toString(16).toUpperCase().padStart(4, "0")}`;
      out += `\\u${low.toString(16).toUpperCase().padStart(4, "0")}`;
    } else {
      out += `\\u${cp.toString(16).toUpperCase().padStart(4, "0")}`;
    }
  }
  return out;
}

/** Percent-encoded URL form (UTF-8 bytes). */
export function urlEncoded(s: string): string {
  return encodeURIComponent(s);
}

export interface CopyFormat {
  key: string;
  label: string;
  /** Short hint shown under the label. */
  hint: string;
  value: string;
}

/** Build the full set of copy formats for a symbol. */
export function allCopyFormats(s: string): CopyFormat[] {
  const named = htmlNamedEntity(s);
  return [
    { key: "raw", label: "Character", hint: "The raw symbol", value: s },
    { key: "uplus", label: "Unicode", hint: "U+ notation", value: uPlusNotation(s) },
    {
      key: "html-hex",
      label: "HTML entity",
      hint: named ? `Hex (named: ${named})` : "Hex numeric reference",
      value: named ?? htmlHexEntity(s),
    },
    {
      key: "html-dec",
      label: "HTML decimal",
      hint: "Decimal numeric reference",
      value: htmlDecimalEntity(s),
    },
    { key: "css", label: "CSS content", hint: "For ::before / ::after", value: cssContent(s) },
    { key: "js", label: "JS / JSON escape", hint: "Surrogate-pair safe", value: jsEscape(s) },
    { key: "url", label: "URL encoded", hint: "Percent-encoded UTF-8", value: urlEncoded(s) },
  ];
}
